// overfade.ts — Zero-setup fade-at-edges for scroll containers (TypeScript, no deps)

type EdgeFlags = {
  top: boolean;
  bottom: boolean;
  left: boolean;
  right: boolean;
};

export type OverfadeOptions = {
  baseFade?: number; // default 24px
  selector?: string; // which nodes to enhance
  observeMutations?: boolean; // auto-attach/detach on DOM changes (default true)
};

type InternalState = {
  el: HTMLElement;
  edges: EdgeFlags;
  fadePx: number;
  onScroll: () => void;
  ro?: ResizeObserver;
};

const DEFAULTS: Required<OverfadeOptions> = {
  baseFade: 24,
  selector:
    ".of-top, .of-bottom, .of-left, .of-right, [data-overfade], [data-of]",
  observeMutations: true,
};

const states = new WeakMap<HTMLElement, InternalState>();
let autoStarted = false;
let autoCleanup: (() => void) | null = null;

/* ----------------------------- Core utilities ----------------------------- */

function parseEdges(el: HTMLElement): EdgeFlags {
  const cls = el.classList;
  const data = (el.dataset.overfade || el.dataset.of || "").toLowerCase();
  const has = (k: string) => cls.contains(`of-${k}`) || data.includes(k);
  const any = has("top") || has("bottom") || has("left") || has("right");
  return {
    top: any ? has("top") : true,
    bottom: any ? has("bottom") : true,
    left: has("left"),
    right: has("right"),
  };
}

function parseLengthMultiplier(el: HTMLElement): number {
  const data = el.dataset.ofLength || el.dataset.overfadeLength;
  if (data && !Number.isNaN(Number(data))) return Number(data);
  const c = Array.from(el.classList).find((x) => x.startsWith("of-length-"));
  if (!c) return 1;
  const raw = c.replace("of-length-", "").replace("_", ".");
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 1;
}

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

function applyMask(el: HTMLElement, edges: EdgeFlags) {
  const sbw = el.offsetWidth - el.clientWidth;
  const sbh = el.offsetHeight - el.clientHeight;

  const style = el.style as CSSStyleDeclaration & {
    webkitMaskImage?: string;
    webkitMaskSize?: string;
    webkitMaskPosition?: string;
    webkitMaskRepeat?: string;
    webkitMaskComposite?: string;
  };

  const t = Number(el.dataset.ofTopPx || "0");
  const b = Number(el.dataset.ofBottomPx || "0");
  const l = Number(el.dataset.ofLeftPx || "0");
  const r = Number(el.dataset.ofRightPx || "0");

  const parts: string[] = [];
  const sizes: string[] = [];
  const pos: string[] = [];
  const reps: string[] = [];
  const comps: string[] = [];
  const wkComps: string[] = [];

  if (edges.top || edges.bottom) {
    parts.push(
      `linear-gradient(to bottom, transparent, black ${t}px, black calc(100% - ${b}px), transparent)`
    );
    sizes.push(`calc(100% - ${sbw}px) 100%`);
    pos.push(`0 0`);
    reps.push(`no-repeat`);
    comps.push(`add`);
    wkComps.push(`source-over`);
  }

  if (edges.left || edges.right) {
    parts.push(
      `linear-gradient(to right, transparent, black ${l}px, black calc(100% - ${r}px), transparent)`
    );
    sizes.push(`100% calc(100% - ${sbh}px)`);
    pos.push(`0 0`);
    reps.push(`no-repeat`);
    comps.push(`intersect`);
    wkComps.push(`destination-in`);
  }

  if (sbw > 0) {
    parts.push(`linear-gradient(black, black)`);
    sizes.push(`${sbw}px 100%`);
    pos.push(`calc(100% - ${sbw}px) 0`);
    reps.push(`no-repeat`);
    comps.push(`add`);
    wkComps.push(`source-over`);
  }

  if (sbh > 0) {
    parts.push(`linear-gradient(black, black)`);
    sizes.push(`100% ${sbh}px`);
    pos.push(`0 calc(100% - ${sbh}px)`);
    reps.push(`no-repeat`);
    comps.push(`add`);
    wkComps.push(`source-over`);
  }

  const img = parts.join(", ");
  const size = sizes.join(", ");
  const position = pos.join(", ");
  const repeat = reps.join(", ");
  const comp = comps.join(", ");
  const wkc = wkComps.join(", ");

  style.maskImage = img;
  style.maskSize = size;
  style.maskPosition = position;
  style.maskRepeat = repeat;
  style.maskComposite = comp;

  style.webkitMaskImage = img;
  style.webkitMaskSize = size;
  style.webkitMaskPosition = position;
  style.webkitMaskRepeat = repeat;
  style.webkitMaskComposite = wkc;
}

function update(el: HTMLElement, st: InternalState) {
  const { edges, fadePx } = st;

  // If hidden (no layout yet), schedule a later refresh
  const hidden =
    el.clientWidth === 0 ||
    el.clientHeight === 0 ||
    getComputedStyle(el).display === "none";
  if (hidden) {
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        if (states.has(el)) update(el, st);
      })
    );
    return;
  }

  const maxY = el.scrollHeight - el.clientHeight;
  const maxX = el.scrollWidth - el.clientWidth;

  if (edges.top || edges.bottom) {
    const topPx = edges.top ? clamp(el.scrollTop, 0, fadePx) : 0;
    const bottomPx = edges.bottom ? clamp(maxY - el.scrollTop, 0, fadePx) : 0;
    el.dataset.ofTopPx = String(topPx);
    el.dataset.ofBottomPx = String(bottomPx);
  } else {
    el.dataset.ofTopPx = "0";
    el.dataset.ofBottomPx = "0";
  }

  if (edges.left || edges.right) {
    const leftPx = edges.left ? clamp(el.scrollLeft, 0, fadePx) : 0;
    const rightPx = edges.right ? clamp(maxX - el.scrollLeft, 0, fadePx) : 0;
    el.dataset.ofLeftPx = String(leftPx);
    el.dataset.ofRightPx = String(rightPx);
  } else {
    el.dataset.ofLeftPx = "0";
    el.dataset.ofRightPx = "0";
  }

  applyMask(el, edges);
}

function normalizeOverflow(el: HTMLElement) {
  const cs = getComputedStyle(el);
  if (cs.overflowX === "visible" && cs.overflowY === "visible") {
    el.style.overflow = "auto";
  }
}

/* ------------------------------- Public API ------------------------------- */

export function attachOverfade(el: HTMLElement, options?: OverfadeOptions) {
  const cfg = { ...DEFAULTS, ...(options || {}) };
  if (states.has(el)) return () => detachOverfade(el);

  normalizeOverflow(el);

  const edges = parseEdges(el);
  const mult = parseLengthMultiplier(el);
  const fadePx = Math.max(1, Math.round(cfg.baseFade * mult));

  const st: InternalState = {
    el,
    edges,
    fadePx,
    onScroll: () => update(el, st),
  };

  states.set(el, st);

  // Initial paint + double RAF to survive portal/modal open animations
  update(el, st);
  requestAnimationFrame(() => requestAnimationFrame(() => update(el, st)));

  el.addEventListener("scroll", st.onScroll, { passive: true });

  // Wrap in rAF to avoid "ResizeObserver loop limit exceeded" in Chrome.
  // Reading layout-forcing properties (offsetWidth/clientWidth) synchronously
  // inside a ResizeObserver callback triggers an infinite loop in Chrome.
  const ro = new ResizeObserver(() => {
    requestAnimationFrame(() => update(el, st));
  });
  ro.observe(el);
  st.ro = ro;

  return () => detachOverfade(el);
}

export function detachOverfade(el: HTMLElement) {
  const st = states.get(el);
  if (!st) return;
  el.removeEventListener("scroll", st.onScroll);
  st.ro?.disconnect();

  const style = el.style as CSSStyleDeclaration & {
    webkitMaskImage?: string;
    webkitMaskSize?: string;
    webkitMaskPosition?: string;
    webkitMaskRepeat?: string;
    webkitMaskComposite?: string;
  };
  style.maskImage =
    style.maskSize =
    style.maskPosition =
    style.maskRepeat =
    style.maskComposite =
      "";
  style.webkitMaskImage =
    style.webkitMaskSize =
    style.webkitMaskPosition =
    style.webkitMaskRepeat =
    style.webkitMaskComposite =
      "";
  delete el.dataset.ofTopPx;
  delete el.dataset.ofBottomPx;
  delete el.dataset.ofLeftPx;
  delete el.dataset.ofRightPx;

  states.delete(el);
}

export function refreshOverfade(el: HTMLElement) {
  const st = states.get(el);
  if (!st) return;
  update(el, st);
  requestAnimationFrame(() => requestAnimationFrame(() => update(el, st)));
}

/* ------------------------- Zero-config global mode ------------------------ */

function findTargets(root: ParentNode, selector: string): HTMLElement[] {
  const arr = Array.from(root.querySelectorAll<HTMLElement>(selector));
  // If root itself matches, include it too:
  if (root instanceof HTMLElement && root.matches(selector)) arr.push(root);
  return arr;
}

export function startOverfadeAuto(options?: OverfadeOptions) {
  if (autoStarted) return autoCleanup || (() => {});
  const cfg = { ...DEFAULTS, ...(options || {}) };

  // 1) Attach to everything initially
  const attached = new Set<HTMLElement>();
  findTargets(document, cfg.selector).forEach((el) => {
    attached.add(el);
    attachOverfade(el, cfg);
  });

  // 2) Observe DOM for additions/removals (works with modals/portals)
  const mo = new MutationObserver((mutations) => {
    let needsRefresh = false;

    for (const m of mutations) {
      // Added
      for (const n of m.addedNodes) {
        if (!(n instanceof Element)) continue;
        findTargets(n, cfg.selector).forEach((el) => {
          if (!attached.has(el)) {
            attached.add(el);
            attachOverfade(el, cfg);
            needsRefresh = true;
          }
        });
      }
      // Removed
      for (const n of m.removedNodes) {
        if (!(n instanceof Element)) continue;
        findTargets(n, cfg.selector).forEach((el) => {
          if (attached.has(el)) {
            attached.delete(el);
            detachOverfade(el);
          }
        });
      }
    }

    if (needsRefresh) {
      // After a batch mount, give layout a tick and refresh all
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          attached.forEach((el) => refreshOverfade(el));
        })
      );
    }
  });
  if (cfg.observeMutations) {
    mo.observe(document.documentElement, { subtree: true, childList: true });
  }

  // 3) Global listeners that help after CSS animations/transitions
  const onResize = () => {
    // throttle to next frame
    requestAnimationFrame(() => attached.forEach((el) => refreshOverfade(el)));
  };
  const onEnd = (e: Event) => {
    // If the event bubbled from within an attached container (e.g., a modal),
    // refresh everything — cheap and robust.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => attached.forEach((el) => refreshOverfade(el)))
    );
  };
  window.addEventListener("resize", onResize, { passive: true });
  document.addEventListener("transitionend", onEnd, true);
  document.addEventListener("animationend", onEnd, true);
  document.addEventListener("visibilitychange", onEnd, true);

  autoCleanup = () => {
    mo.disconnect();
    window.removeEventListener("resize", onResize);
    document.removeEventListener("transitionend", onEnd, true);
    document.removeEventListener("animationend", onEnd, true);
    document.removeEventListener("visibilitychange", onEnd, true);
    attached.forEach((el) => detachOverfade(el));
    attached.clear();
    autoCleanup = null;
    autoStarted = false;
  };

  autoStarted = true;
  return autoCleanup;
}

export function stopOverfadeAuto() {
  if (autoCleanup) autoCleanup();
}

export default startOverfadeAuto;
